// Terminal simulyatoru (KT-91): test və nümayiş üçün. Real bank adapteri bankın ECR sənədi / SDK-sı gələndə yazılır.
// Qəpiyə görə davranış (təkrarlanan test): .13 — rədd; .99 — cavab gəlmir, status sorğusu təsdiqi tapır;
// .98 — cavab da, status da gəlmir (needs_review). Qalanı — təsdiq. Kart nömrəsi yoxdur: son 4 = 4242.
import type { CardTxnResult, PosAdapter } from '@kassa/shared';
import { PosTimeout } from './flow';

interface SimTxn {
  id: string;
  kind: 'sale' | 'refund' | 'void';
  amountKop: number;
  result: CardTxnResult;
}

interface SimState {
  batch: number;
  txns: SimTxn[];
}

function kopOf(amount: string): number {
  const [i, f = ''] = amount.split('.');
  return Number(i) * 100 + Number((f + '00').slice(0, 2));
}

function amountOf(kop: number): string {
  return `${Math.trunc(kop / 100)}.${String(Math.abs(kop % 100)).padStart(2, '0')}`;
}

export class SimulatorAdapter implements PosAdapter {
  private mem: SimState | null = null;

  constructor(
    private readonly terminalId: string,
    private readonly tid: string | null,
    private readonly delayMs = 700,
  ) {}

  private key() {
    return `kassa.pos.sim.${this.terminalId}`;
  }

  private load(): SimState {
    if (this.mem) return this.mem;
    try {
      const raw = window.localStorage.getItem(this.key());
      const v = raw ? (JSON.parse(raw) as Partial<SimState>) : null;
      this.mem = v && Array.isArray(v.txns) && typeof v.batch === 'number' ? (v as SimState) : { batch: 1, txns: [] };
    } catch (e) {
      if (typeof window !== 'undefined') console.warn('kassa.pos.sim: yaddaş oxunmadı, təmiz başlanğıc', e instanceof Error ? e.message : e);
      this.mem = { batch: 1, txns: [] };
    }
    return this.mem;
  }

  private save(s: SimState) {
    this.mem = s;
    try {
      window.localStorage.setItem(this.key(), JSON.stringify(s));
    } catch {
      /* gizli pəncərə — yalnız yaddaşda */
    }
  }

  private wait() {
    return new Promise((r) => setTimeout(r, this.delayMs));
  }

  private approve(id: string, kind: SimTxn['kind'], kop: number, s: SimState): CardTxnResult {
    const result: CardTxnResult = {
      status: 'approved',
      rrn: `SIM${Date.now().toString().slice(-9)}`,
      authCode: Math.random().toString(36).slice(2, 8).toUpperCase(),
      cardLast4: '4242',
      cardType: 'VISA',
      terminalTid: this.tid,
      batchNo: String(s.batch).padStart(4, '0'),
    };
    s.txns.push({ id, kind, amountKop: kop, result });
    this.save(s);
    return result;
  }

  async sale(req: { txnId: string; amount: string }): Promise<CardTxnResult> {
    await this.wait();
    const s = this.load();
    const prev = s.txns.find((t) => t.id === req.txnId);
    if (prev) return prev.result; // terminal eyni əməliyyatı iki dəfə çıxmır
    const kop = kopOf(req.amount);
    const cents = kop % 100;
    if (cents === 13) {
      const result: CardTxnResult = { status: 'declined', message: 'Rədd: balans çatmır (simulyator)', terminalTid: this.tid };
      s.txns.push({ id: req.txnId, kind: 'sale', amountKop: kop, result });
      this.save(s);
      return result;
    }
    if (cents === 99) {
      this.approve(req.txnId, 'sale', kop, s); // pul çıxıb, amma cavab kassaya çatmayıb
      throw new PosTimeout('Terminal cavab vermədi');
    }
    if (cents === 98) throw new PosTimeout('Terminal cavab vermədi');
    return this.approve(req.txnId, 'sale', kop, s);
  }

  async refund(req: { txnId: string; amount: string; origRrn: string }): Promise<CardTxnResult> {
    await this.wait();
    return this.approve(req.txnId, 'refund', kopOf(req.amount), this.load());
  }

  async void(req: { txnId: string; origRrn: string }): Promise<CardTxnResult> {
    await this.wait();
    const s = this.load();
    const orig = s.txns.find((t) => t.result.rrn === req.origRrn);
    return this.approve(req.txnId, 'void', orig?.amountKop ?? 0, s);
  }

  async status(txnId: string): Promise<CardTxnResult | null> {
    await this.wait();
    const s = this.load();
    const t = s.txns.find((x) => x.id === txnId);
    if (t) return t.result;
    // .98 halı: terminalla əlaqə yoxdur — status da alınmır
    throw new PosTimeout('Terminalla əlaqə yoxdur');
  }

  async batchClose(): Promise<{ batchNo: string; closedAt: string; count: number; total: string }> {
    await this.wait();
    const s = this.load();
    const batchNo = String(s.batch).padStart(4, '0');
    const done = s.txns.filter((t) => t.result.status === 'approved' && t.result.batchNo === batchNo);
    const total = done.reduce((sum, t) => sum + (t.kind === 'sale' ? t.amountKop : -t.amountKop), 0);
    const count = done.filter((t) => t.kind === 'sale').length;
    this.save({ batch: s.batch + 1, txns: s.txns.filter((t) => t.result.batchNo !== batchNo) });
    return { batchNo, closedAt: new Date().toISOString(), count, total: amountOf(total) };
  }
}
